import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  TextField, 
  Button, 
  Box, 
  CircularProgress,
  Alert
} from '@mui/material';
interface AuthStepProps {
  onVerifyPin: (pin: string) => Promise<{ success: boolean; message?: string }>;
  token: string;
  skipAuthMessage?: string;
}

const AuthenticationStep: React.FC<AuthStepProps> = ({ onVerifyPin, skipAuthMessage }) => {
  const [pin, setPin] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  
  // If we have a skipAuthMessage, automatically skip this step
  useEffect(() => {
    if (skipAuthMessage) {
      setIsVerifying(true);
      // This timeout is just to show a brief loading message before proceeding
      const timer = setTimeout(() => {
        // Simulate a successful verification
        onVerifyPin('auto-verified');
      }, 1500);
      
      return () => clearTimeout(timer);
    }
  }, [skipAuthMessage, onVerifyPin]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    
    // Validate PIN format (a resource number is numeric)
    if (!/^\d+$/.test(pin) || pin.length === 0) {
      setError('PIN must be numeric');
      return;
    }
    
    try {
      setIsVerifying(true);
      setError('');
      
      const result = await onVerifyPin(pin);
      
      if (!result.success) {
        setError(result.message || 'Invalid PIN. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
      console.error('PIN verification error:', err);
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Card sx={{ maxWidth: 500, width: '100%' }}>
        <CardContent>
          <Typography variant="h2" component="h2" gutterBottom>
            Secure Access
          </Typography>
          
          {skipAuthMessage ? (
            <>
              <Alert severity="info" sx={{ mb: 3 }}>
                {skipAuthMessage}
              </Alert>
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            </>
          ) : (
            <>
              <Typography variant="body1" sx={{ mb: 3 }}>
                Please enter the PIN code that was sent to your email to access
                this form.
              </Typography>
              
              <form onSubmit={handleSubmit}>
                <TextField
                  id="pin-input"
                  label="PIN Code"
                  type="text"
                  inputMode="numeric"
                  inputProps={{
                    pattern: '[0-9]*'
                  }}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="Enter your PIN"
                  variant="outlined"
                  fullWidth
                  required
                  error={!!error}
                  helperText={error}
                  sx={{ mb: 3 }}
                />
                
                <Button 
                  type="submit" 
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  disabled={isVerifying || pin.length === 0}
                  startIcon={isVerifying ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {isVerifying ? 'Verifying...' : 'Continue'}
                </Button>
              </form>
              
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  If you&apos;re having trouble accessing the form, please contact support.
                </Typography>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AuthenticationStep;